import { useMemo, useCallback } from 'react';

export const useStepStatus = (currentStep, stepGroups) => {
  const isTerminal = currentStep === 'contactSales';

  // Find current group and substep index
  const currentGroupInfo = useMemo(() => {
    for (const group of stepGroups) {
      // Check single steps
      if (group.type === 'single' && group.stepKey === currentStep) {
        return { group, substepIndex: -1 };
      }

      // Check group substeps
      if (group.type === 'group') {
        const substepIndex = group.substeps.findIndex(
          (sub) => sub.key === currentStep
        );
        if (substepIndex !== -1) {
          return { group, substepIndex };
        }
      }
    }
    return { group: null, substepIndex: -1 };
  }, [currentStep, stepGroups]);

  // Get status for a group/single step
  const getGroupStatus = useCallback(
    (group) => {
      if (isTerminal) return group.key === 'contactSales' ? 'process' : 'finish';

      // Current step
      if (currentGroupInfo.group?.key === group.key) {
        return 'process';
      }

      // Find positions
      const groupIndex = stepGroups.findIndex((g) => g.key === group.key);
      const currentGroupIndex = stepGroups.findIndex(
        (g) => g.key === currentGroupInfo.group?.key
      );

      // Completed step
      if (groupIndex < currentGroupIndex) return 'finish';

      // Future step
      return 'wait';
    },
    [currentGroupInfo.group?.key, isTerminal, stepGroups]
  );

  // Get status for a substep within a group
  const getSubstepStatus = useCallback(
    (group, substepIndex) => {
      if (isTerminal) return 'finish';

      // Current substep
      if (
        currentGroupInfo.group?.key === group.key &&
        currentGroupInfo.substepIndex === substepIndex
      ) {
        return 'process';
      }

      // Substep in current group
      if (currentGroupInfo.group?.key === group.key) {
        if (substepIndex < currentGroupInfo.substepIndex) return 'finish';
        return 'wait';
      }

      // Substep in different group
      const groupIndex = stepGroups.findIndex((g) => g.key === group.key);
      const currentGroupIndex = stepGroups.findIndex(
        (g) => g.key === currentGroupInfo.group?.key
      );

      if (groupIndex < currentGroupIndex) return 'finish';
      return 'wait';
    },
    [
      currentGroupInfo.group?.key,
      currentGroupInfo.substepIndex,
      isTerminal,
      stepGroups,
    ]
  );

  // Check if a step can be navigated to (completed or current)
  const canNavigateToStep = useCallback(
    (stepKey) => {
      for (const group of stepGroups) {
        // Check single steps
        if (group.type === 'single' && group.stepKey === stepKey) {
          const status = getGroupStatus(group);
          return status === 'finish' || status === 'process';
        }

        // Check substeps
        if (group.type === 'group') {
          const substep = group.substeps.find((sub) => sub.key === stepKey);
          if (substep) {
            const substepIndex = group.substeps.indexOf(substep);
            const status = getSubstepStatus(group, substepIndex);
            return status === 'finish' || status === 'process';
          }
        }
      }
      return false;
    },
    [stepGroups, getGroupStatus, getSubstepStatus]
  );

  return {
    currentGroupInfo,
    getGroupStatus,
    getSubstepStatus,
    canNavigateToStep,
  };
};

export default useStepStatus;
